import { useState } from "react";
import { Calculator, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  usePreviewPickupQuote,
  type PreviewPickupQuoteResult,
} from "@/api/preview-pickup-quote";

/**
 * Inline quote-preview panel for the rate workspace.
 *
 * Admin enters a sample (sender area, recipient area, weight) tuple; the
 * server runs the same `calculatePickupLegs` + `findActiveCarrierRateBand`
 * path the customer quote uses and returns the breakdown. Lets admin
 * sanity-check what customers actually pay against the raw band cells
 * shown below in the route table.
 *
 * Reads from-area and to-area choices off the workspace's serviceAreas
 * list; carrier/pickup/dispatch context comes from the workspace scope.
 * Disabled until the scope is fully picked.
 */
export function QuotePreviewPanel({
  carrierServiceId,
  pickupServiceId,
  dispatchCenterId,
  destinationCarrierId,
  serviceAreas,
}: {
  carrierServiceId: string | null;
  pickupServiceId: string | null;
  dispatchCenterId: string | null;
  destinationCarrierId: string | null;
  serviceAreas: { id: string; name: string }[];
}) {
  const [fromAreaId, setFromAreaId] = useState<string>("");
  const [toAreaId, setToAreaId] = useState<string>("");
  const [actualKg, setActualKg] = useState<string>("1");
  const [lengthCm, setLengthCm] = useState<string>("30");
  const [widthCm, setWidthCm] = useState<string>("20");
  const [heightCm, setHeightCm] = useState<string>("15");
  const [result, setResult] = useState<PreviewPickupQuoteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = usePreviewPickupQuote();

  const scopeReady =
    !!carrierServiceId &&
    !!pickupServiceId &&
    !!dispatchCenterId &&
    !!destinationCarrierId;
  const allPos = [actualKg, lengthCm, widthCm, heightCm].every(
    (v) => parseFloat(v) > 0 && Number.isFinite(parseFloat(v))
  );
  const inputsReady = !!fromAreaId && !!toAreaId && allPos;

  const fmtCents = (cents: number | null) =>
    cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;
  const fmtKg = (kg: number) => `${kg.toFixed(2)} kg`;

  async function handlePreview() {
    if (!scopeReady || !inputsReady) return;
    setError(null);
    try {
      const data = await preview.mutateAsync({
        carrierServiceId: carrierServiceId,
        carrierAddOnServiceId: pickupServiceId,
        dispatchCenterId: dispatchCenterId,
        destinationCarrierId: destinationCarrierId,
        fromServiceAreaId: fromAreaId,
        toServiceAreaId: toAreaId,
        actualWeightKg: parseFloat(actualKg),
        lengthCm: parseFloat(lengthCm),
        widthCm: parseFloat(widthCm),
        heightCm: parseFloat(heightCm),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed");
      setResult(null);
    }
  }

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium text-blue-900">
          <Calculator className="h-4 w-4" />
          Quote preview
          <span className="text-blue-700 font-normal">
            — runs the customer quote engine for a sample shipment
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] text-blue-900">From area</Label>
            <Select value={fromAreaId} onValueChange={setFromAreaId}>
              <SelectTrigger className="h-8 text-xs bg-white">
                <SelectValue placeholder="Sender" />
              </SelectTrigger>
              <SelectContent>
                {serviceAreas.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] text-blue-900">To area</Label>
            <Select value={toAreaId} onValueChange={setToAreaId}>
              <SelectTrigger className="h-8 text-xs bg-white">
                <SelectValue placeholder="Recipient" />
              </SelectTrigger>
              <SelectContent>
                {serviceAreas.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 w-[72px]">
            <Label className="text-[10px] text-blue-900">Weight kg</Label>
            <Input
              type="number"
              min={0}
              step="0.1"
              value={actualKg}
              onChange={(e) => setActualKg(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1 w-[64px]">
            <Label className="text-[10px] text-blue-900">L cm</Label>
            <Input
              type="number"
              min={0}
              step="0.1"
              value={lengthCm}
              onChange={(e) => setLengthCm(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1 w-[64px]">
            <Label className="text-[10px] text-blue-900">W cm</Label>
            <Input
              type="number"
              min={0}
              step="0.1"
              value={widthCm}
              onChange={(e) => setWidthCm(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1 w-[64px]">
            <Label className="text-[10px] text-blue-900">H cm</Label>
            <Input
              type="number"
              min={0}
              step="0.1"
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <Button
            size="sm"
            onClick={() => void handlePreview()}
            disabled={!scopeReady || !inputsReady || preview.isPending}
            className="h-8"
          >
            {preview.isPending && (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            )}
            Preview
          </Button>
        </div>

        {!scopeReady && (
          <p className="text-[11px] text-muted-foreground italic">
            Pick a carrier service, pickup service, and dispatch above to use
            the preview.
          </p>
        )}

        {error && <p className="text-[11px] text-destructive">{error}</p>}

        {result && (
          <div className="rounded-md border bg-white p-3 text-xs space-y-1">
            <Row label={`Label leg @ ${fmtKg(result.labelBillableKg)}`}>
              {result.labelMissing ? (
                <span className="text-amber-700">
                  unpriced lane — set a label rate
                </span>
              ) : (
                fmtCents(result.labelLegCents)
              )}
            </Row>
            <Row label={`Pickup leg @ ${fmtKg(result.pickupBillableKg)}`}>
              {result.pickupError ? (
                <span className="text-amber-700">
                  {result.pickupError.code}
                </span>
              ) : (
                fmtCents(result.pickupLegCents)
              )}
            </Row>
            <Row label={`Redist leg @ ${fmtKg(result.pickupBillableKg)}`}>
              {result.pickupError ? (
                <span className="text-amber-700">—</span>
              ) : (
                fmtCents(result.redistLegCents)
              )}
            </Row>
            {result.labelBillableKg !== result.pickupBillableKg && (
              <p className="text-[10px] text-muted-foreground italic pt-1">
                Label and pickup priced against different billable weights —
                each leg uses its own dim factor.
              </p>
            )}
            <div className="border-t mt-2 pt-2 flex items-center justify-between font-semibold text-blue-900">
              <span>Customer pays (pickup)</span>
              <span>{fmtCents(result.totalPickupCents)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{children}</span>
    </div>
  );
}
